<template>
  <div class="container">
    <!-- Success and Error Messages -->
    <div v-if="successMessage" class="alert alert-success alert-dismissible fade show fixed-alert" role="alert">
      {{ successMessage }}
      <button type="button" class="btn-close" @click="successMessage = ''"></button>
    </div>

    <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show fixed-alert" role="alert">
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <!-- Page Title -->
    <div class="mt-4">
      <h2>{{ $t('job_levels') }}</h2>
    </div>

    <!-- Add Job Level Button -->
    <div class="mb-4">
      <button v-if="hasPermission('users.add_joblevel')" class="btn btn-primary" @click="openModal('add')">
        <i class="bi bi-plus-circle"></i> {{ $t('add_job_level') }}
      </button>
    </div>

    <!-- Job Level List -->
    <div class="columns is-multiline">
      <div class="column is-12">
        <template v-if="jobLevels.length">
          <table class="table table-hover">
            <thead>
            <tr>
              <th>{{ $t('id') }}</th>
              <th>{{ $t('name') }}</th>
              <th>{{ $t('level') }}</th>
              <th>{{ $t('actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="jobLevel in jobLevels" :key="jobLevel.id">
              <td>{{ jobLevel.id }}</td>
              <td>{{ jobLevel.name }}</td>
              <td>{{ jobLevel.level }}</td>
              <td>
                <!-- Edit button -->
                <button v-if="hasPermission('users.change_joblevel')" class="btn btn-primary btn-sm"
                        @click="openModal('edit', jobLevel)">
                  <i class="bi bi-pencil-square"></i> {{ $t('edit') }}
                </button>
                <!-- Delete button -->
                <button v-if="hasPermission('users.delete_joblevel')" @click="openConfirmDelete(jobLevel.id)"
                        class="btn btn-danger btn-sm">
                  <i class="bi bi-trash"></i> {{ $t('delete') }}
                </button>
              </td>
            </tr>
            </tbody>
          </table>

          <!-- Pagination Buttons -->
          <div class="buttons">
            <button class="btn btn-light" @click="goToPreviousPage" v-if="showPreviousButton">{{ $t('previous') }}</button>
            <button class="btn btn-light" @click="goToNextPage" v-if="showNextButton">{{ $t('next') }}</button>
          </div>
        </template>

        <!-- No job levels available -->
        <div v-else>
          <p>{{ $t('no_job_levels_found') }}</p>
        </div>
      </div>
    </div>

    <!-- Bootstrap Modal for Add/Edit Job Level -->
    <div class="modal fade" id="jobLevelModal" tabindex="-1" aria-labelledby="jobLevelModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="jobLevelModalLabel">{{ isEditMode ? $t('edit_job_level') : $t('add_job_level') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <!-- Add/Edit Form -->
            <form @submit.prevent="submitJobLevel">
              <div class="mb-3">
                <label for="jobLevelName" class="form-label">{{ $t('job_level_name') }}</label>
                <input v-model="jobLevelForm.name" type="text" class="form-control" id="jobLevelName" required/>
              </div>
              <div class="mb-3">
                <label for="jobLevelLevel" class="form-label">{{ $t('level') }}</label>
                <input v-model="jobLevelForm.level" type="text" class="form-control" id="jobLevelLevel" required/>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="submitJobLevel">{{ isEditMode ? $t('update') : $t('add') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Confirm Delete Modal -->
    <div v-if="showConfirm" class="modal-overlay">
      <div class="modal-container">
        <h3 class="modal-title">{{ $t('confirm_delete') }}</h3>
        <p>{{ $t('confirm_delete_job_level') }}</p>
        <div class="modal-actions">
          <button class="btn btn-danger" @click="deleteJobLevel">{{ $t('yes_delete') }}</button>
          <button class="btn btn-primary" @click="cancelDelete">{{ $t('cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { useMainStore } from '@/store';
import axios from 'axios';

export default {
  name: 'JobLevelList',
  data() {
    return {
      jobLevels: [],
      showNextButton: false,
      showPreviousButton: false,
      currentPage: 1,
      successMessage: '',
      errorMessage: '',
      isEditMode: false,
      jobLevelForm: {
        id: null,
        name: '',
        level: ''
      },
      modalInstance: null,
      showConfirm: false, // Show custom confirm modal
      jobLevelIdToDelete: null // Store the job level ID to delete
    };
  },
  mounted() {
    this.getJobLevels();
    this.$nextTick(() => {
      const modalElement = document.getElementById('jobLevelModal');
      if (modalElement) {
        this.modalInstance = new bootstrap.Modal(modalElement);
      }
    });
  },
  computed: {
    store: () => useMainStore(),
    hasPermission() {
      return (permission) => {
        const userPermissions = this.store.user.permissions || [];
        return userPermissions.includes(permission);
      };
    }
  },

  methods: {
    openModal(mode, jobLevel = null) {
      if (mode === 'add') {
        this.isEditMode = false;
        this.jobLevelForm = {id: null, name: '', level: ''}; // Reset form for adding
      } else if (mode === 'edit') {
        this.isEditMode = true;
        this.jobLevelForm = {...jobLevel}; // Populate form for editing
      }
      this.modalInstance.show(); // Show Bootstrap modal
    },
    closeModal() {
      this.modalInstance.hide(); // Hide Bootstrap modal
    },
    async submitJobLevel() {
      if (this.isEditMode) {
        // Edit existing job level
        try {
          await axios.put(`/api/v1/admin/job-levels/${this.jobLevelForm.id}/`, this.jobLevelForm);
          this.successMessage = 'Job level updated successfully';
          this.errorMessage = '';
          this.getJobLevels(); // Refresh job levels list
        } catch (error) {
          this.errorMessage = 'Failed to update job level';
          this.successMessage = '';
        }
      } else {
        // Add new job level
        try {
          await axios.post(`/api/v1/admin/job-levels/`, this.jobLevelForm);
          this.successMessage = 'Job level added successfully';
          this.errorMessage = '';
          this.getJobLevels(); // Refresh job levels list
        } catch (error) {
          this.errorMessage = 'Failed to add job level';
          this.successMessage = '';
        }
      }

      // Close the modal and reset form
      this.closeModal();
      this.jobLevelForm = {id: null, name: '', level: ''};

      // Hide the message after 3 seconds
      setTimeout(() => {
        this.successMessage = '';
        this.errorMessage = '';
      }, 3000);
    },
    openConfirmDelete(jobLevelId) {
      this.jobLevelIdToDelete = jobLevelId;
      this.showConfirm = true; // Show custom confirm modal
    },
    async deleteJobLevel() {
      try {
        await axios.delete(`/api/v1/admin/job-levels/${this.jobLevelIdToDelete}/`);
        this.successMessage = 'Job level deleted successfully';
        this.errorMessage = '';
        this.getJobLevels(); // Refresh the list after deletion
      } catch (error) {
        this.errorMessage = 'Failed to delete job level';
        this.successMessage = '';
      }
      this.showConfirm = false; // Hide confirm modal
    },
    cancelDelete() {
      this.showConfirm = false; // Hide confirm modal
    },
    async getJobLevels() {
      this.showNextButton = false;
      this.showPreviousButton = false;

      await axios
          .get(`/api/v1/admin/job-levels/?page=${this.currentPage}`)
          .then(response => {
            this.jobLevels = response.data.results;

            if (response.data.next) {
              this.showNextButton = true;
            }

            if (response.data.previous) {
              this.showPreviousButton = true;
            }
          })
          .catch(error => {
            this.errorMessage = 'Failed to fetch job levels';
            console.log(error);
          });
    },
    goToNextPage() {
      this.currentPage += 1;
      this.getJobLevels();
    },
    goToPreviousPage() {
      this.currentPage -= 1;
      this.getJobLevels();
    }
  }
};
</script>
